import type { AgentTaskList } from "@opencloud/agent-protocol";
import { Ban, Check, ChevronDown, Circle, CircleAlert, ListChecks, LoaderCircle, Pause, RotateCcw } from "lucide-react";
import { Button, Card, Progress } from "@opencloud/ui";
import { useId } from "react";
import { taskListPresentation, type TaskListLifecycle } from "../task-presentation.js";
import type { RunStatus } from "../types.js";

export function TaskListRenderer({
  taskList, collapsible = false, collapsed = false, onToggleCollapse,
  lifecycle = "active", terminalTitle, retryLabel, onRetry,
}: {
  taskList: AgentTaskList;
  trustedStatus?: string;
  collapsible?: boolean;
  collapsed?: boolean;
  onToggleCollapse?: () => void;
  lifecycle?: TaskListLifecycle;
  terminalEyebrow?: string | undefined;
  terminalTitle?: string | undefined;
  retryLabel?: string | undefined;
  onRetry?: (() => void) | undefined;
}) {
  const id = useId();
  const view = taskListPresentation(taskList, lifecycle);
  const heading = <>
    <span className="a3-plan-icon" aria-hidden="true"><TaskPlanIcon view={view} /></span>
    <span className="a3-plan-heading"><strong>{terminalTitle ?? (collapsed ? view.title : "Agent plan")}</strong>
      <span>{collapsed ? view.count : view.title + " · " + view.count}</span></span>
    {collapsible ? <ChevronDown className="a3-plan-chevron" data-open={!collapsed} aria-hidden="true" /> : null}
  </>;
  return <Card className="a3-agent-card a3-task-list" data-card-type="task_list"
    data-state={lifecycle === "active" ? "live" : lifecycle} data-tone={view.tone}
    data-revision={taskList.revision} data-collapsed={collapsed}>
    {collapsible ? <Button type="button" variant="ghost" className="a3-plan-toggle"
      aria-label={collapsed ? "Expand agent plan" : "Collapse agent plan"}
      aria-expanded={!collapsed} aria-controls={id} onClick={onToggleCollapse}>{heading}</Button>
      : <div className="a3-plan-toggle">{heading}</div>}
    <TaskProgress taskList={taskList} />
    {!collapsed ? <div id={id} className="a3-plan-content">
      <TaskPlanItems taskList={taskList} lifecycle={lifecycle} />
      {retryLabel && onRetry ? <Button type="button" variant="ghost" onClick={onRetry}>
        <RotateCcw aria-hidden="true" />{retryLabel}
      </Button> : null}
    </div> : null}
  </Card>;
}

export function TaskPlanIcon({ view }: { view: ReturnType<typeof taskListPresentation> }) {
  if (view.moving) return <LoaderCircle className="a3-spin" />;
  if (view.status === "Failed") return <CircleAlert />;
  if (view.status === "Canceled" || view.status === "Stopping") return <Ban />;
  if (["Paused", "Pausing", "Needs reply"].includes(view.status)) return <Pause />;
  if (view.tone === "success") return <Check />;
  return <ListChecks />;
}

export function TaskProgress({ taskList }: { taskList: AgentTaskList }) {
  const completed = taskList.items.filter((item) => item.status === "completed").length;
  return <Progress className="a3-plan-progress" value={taskList.items.length ? completed / taskList.items.length * 100 : 0}
    aria-label={completed + " of " + taskList.items.length + " tasks completed"} />;
}

export function TaskPlanItems({ taskList, lifecycle, runStatus }: {
  taskList: AgentTaskList; lifecycle: TaskListLifecycle; runStatus?: RunStatus;
}) {
  const paused = runStatus === "paused" || runStatus === "waiting_for_user" || runStatus === "pausing";
  return <ol aria-label="Agent tasks" tabIndex={taskList.items.length > 6 ? 0 : undefined}>
    {taskList.items.map((item) => {
      const frozen = item.status === "in_progress" && (lifecycle !== "active" || paused || runStatus === "cancelling" || runStatus === "queued");
      const status = frozen ? paused ? "Paused" : "Interrupted"
        : { pending: "Pending", in_progress: "In progress", completed: "Completed", cancelled: "Canceled" }[item.status];
      return <li key={item.id} data-status={frozen ? "interrupted" : item.status} aria-label={item.label + ", " + status}>
        <span className="a3-task-list-icon" aria-hidden="true">
          {frozen ? <Pause /> : item.status === "completed" ? <Check />
            : item.status === "in_progress" ? <LoaderCircle className="a3-spin" />
            : item.status === "cancelled" ? <Ban /> : <Circle />}
        </span>
        <span className="a3-task-list-label">{item.label}<small>{status}</small></span>
      </li>;
    })}
  </ol>;
}
